/**
 * <sticky-atc> — mobile sticky add-to-cart bar.
 *
 * Deliberately small. Two jobs only:
 *
 *   1. Visibility. One IntersectionObserver on the in-page add-to-cart button
 *      (`#ProductSubmitButton-<sectionId>`, rendered by snippets/buy-buttons.liquid:83). The bar
 *      is revealed only once that button has scrolled *above* the viewport, so it never appears
 *      before the shopper has reached the real add-to-cart control.
 *
 *   2. Price + disabled sync. Subscribes to PUB_SUB_EVENTS.variantChange, published by
 *      assets/product-info.js:220. The payload is nested: `{ data: { sectionId, html, variant } }`
 *      — read via `event.data.*`, matching assets/price-per-item.js:22 and
 *      assets/recipient-form.js:40. `html` is the freshly rendered section document, so the new
 *      price and the new disabled state can be copied straight out of it with no extra fetch.
 *
 * Adding to cart needs no code here at all: the bar's button is a `type="submit"` associated to
 * the product form via the HTML `form` attribute, so it fires the `submit` event that
 * assets/product-form.js:10 already binds. Variant and quantity come along automatically.
 *
 * `constants.js` (PUB_SUB_EVENTS) and `pubsub.js` (subscribe) are already loaded globally from
 * layout/theme.liquid:74-75 — nothing is re-imported here.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.priceTarget = this.querySelector(`#StickyAtcPrice-${this.sectionId}`);
        this.submitButton = this.querySelector('[type="submit"]');
        this.variantChangeUnsubscriber = undefined;
        this.observer = undefined;
      }

      connectedCallback() {
        // JS is running, so the price/disabled sync below is live: the bar may now be shown.
        // It stays translated off-screen until the observer sets [data-visible].
        this.hidden = false;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.updateFromSectionHtml(event.data.html);
        });

        this.observeInPageButton();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }

        if (this.observer) {
          this.observer.disconnect();
          this.observer = undefined;
        }
      }

      observeInPageButton() {
        const anchor = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!anchor) return;

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            // Show only when the in-page button has left the viewport upwards. Checking
            // boundingClientRect.top < 0 keeps the bar hidden while the button is still below
            // the fold, so it appears on the way back up the page, never before.
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggleAttribute('data-visible', scrolledPast);
          });
        });

        this.observer.observe(anchor);
      }

      /**
       * @param {Document} html Freshly rendered section document from the variantChange payload.
       */
      updateFromSectionHtml(html) {
        const newPrice = html.getElementById(`price-${this.sectionId}`);
        if (newPrice && this.priceTarget) {
          this.priceTarget.innerHTML = newPrice.innerHTML;
        }

        if (!this.submitButton) return;

        const newButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        // No button in the new markup means the variant cannot be bought — fail closed.
        const disabled = newButton ? newButton.hasAttribute('disabled') : true;
        this.submitButton.toggleAttribute('disabled', disabled);

        // Mirror the label too, so the bar reads "Sold out" / "Unavailable" alongside the
        // in-page button rather than still offering "Add to cart".
        const newLabel = newButton?.querySelector('span')?.textContent;
        const labelTarget = this.submitButton.querySelector('span');
        if (newLabel && labelTarget) {
          labelTarget.textContent = newLabel.trim();
        }
      }
    }
  );
}
